import type { Booking, Deposit } from '@/domain/types';
import {
  Send, Eye, CheckCircle2, CalendarPlus, AlertTriangle, RotateCw, CalendarX, Ban,
} from 'lucide-react';

export type DepositDisplayStatus =
  | 'sent' | 'viewed' | 'paid' | 'placed' | 'lapsed' | 'forfeited' | 'expired' | 'link-resent';

/**
 * Money down but the session was a no-show. NOTE: the runbook also floated "or
 * the session date passed unattended", but this seed leaves finished sessions as
 * `confirmed` rather than `completed`, so a past-date heuristic mislabels ~1000
 * healthy placed deposits as lapsed. We therefore key `lapsed` off the explicit
 * `no-show` mark only (the reliable signal; G3 wires the no-show action to it).
 */
function isLapsed(b: Booking): boolean {
  return b.status === 'no-show';
}

/**
 * L2 (17 Jul): the money is actually kept once a `forfeit` movement lands (past
 * the 8h window). `lapsed` is the earlier state - paid + no-showed but not yet
 * forfeited. A later `reinstate` reverses the forfeit, so we treat the deposit
 * as forfeited only while its most recent forfeit is unreversed.
 */
function isForfeited(d: Deposit): boolean {
  const ledger = d.ledger ?? [];
  let lastForfeit = -1;
  let lastReinstate = -1;
  ledger.forEach((e, i) => {
    if (e.kind === 'forfeit') lastForfeit = i;
    if (e.kind === 'reinstate') lastReinstate = i;
  });
  return lastForfeit >= 0 && lastReinstate < lastForfeit;
}

/**
 * Single source of truth for a deposit's display status (ledger + artist list).
 * Pass the linked booking to surface the G2 `lapsed` / `forfeited` states.
 */
export function deriveDepositStatus(d: Deposit, nowMs: number, booking?: Booking): DepositDisplayStatus {
  const pastExpiry = new Date(d.expiresAt).getTime() < nowMs;
  if (d.status === 'expired') return 'expired';
  if (d.status === 'pending' && pastExpiry) return 'expired';
  // Ledger evidence (an unreversed forfeit) wins over the booking-status lapsed
  // read, so paid -> lapsed -> forfeited reads as one progression on the drawer.
  if (isForfeited(d)) return 'forfeited';
  if (d.status === 'paid' && booking && isLapsed(booking)) return 'lapsed';
  if (d.status === 'paid' && d.bookingId) return 'placed';
  if (d.status === 'paid') return 'paid';
  if (d.resentAt) return 'link-resent';
  if (d.viewedAt) return 'viewed';
  return 'sent';
}

/** Whether a deposit link was sent by an artist (self-book) or the office. */
export function depositOrigin(d: Deposit): 'artist' | 'office' {
  if (d.source === 'artist-link' || (d.depositReference && !d.takenByAgentId)) return 'artist';
  return 'office';
}

export const DEPOSIT_STATUS_META: Record<
  DepositDisplayStatus,
  { label: string; tone: string; icon: typeof Send }
> = {
  sent: { label: 'Sent', tone: 'bg-cream-soft/15 text-cream-soft', icon: Send },
  viewed: { label: 'Viewed', tone: 'bg-gold/20 text-gold-bright', icon: Eye },
  paid: { label: 'Paid', tone: 'bg-mint/20 text-mint', icon: CheckCircle2 },
  placed: { label: 'Placed', tone: 'bg-mint/30 text-mint', icon: CalendarPlus },
  lapsed: { label: 'Lapsed', tone: 'bg-blood/25 text-blood', icon: CalendarX },
  // Distinct muted ochre (not blood-red) - the deposit is kept, but reversible.
  forfeited: { label: 'Forfeited', tone: 'bg-orange/20 text-orange', icon: Ban },
  expired: { label: 'Expired', tone: 'bg-blood/20 text-blood', icon: AlertTriangle },
  'link-resent': { label: 'Link resent', tone: 'bg-azure/15 text-azure', icon: RotateCw },
};
